import { PageProps, Script } from "gatsby";
import { GlobalContext } from "components/stores/global-context";
import { useContext } from "react";
import { ContentfulContentPageContext, ContentfulContentPageData } from "../../templates/content-page";
import { FacebookHelper } from "components/helpers";
import { Html, LinkTags, MetaTags, Title } from "@parksandresorts/core";

type Props = PageProps<ContentfulContentPageData, ContentfulContentPageContext>;

const seoLinkedData = (logoUrl: string) =>
  JSON.stringify(
    {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: "Gröna Lund",
      url: "https://www.gronalund.com",
      logo: logoUrl,
      address: {
        "@type": "PostalAddress",
        streetAddress: "Lilla Allmänna Gränd 9",
        addressLocality: "Stockholm",
        postalCode: "115 21",
        addressCountry: "Sweden",
      },
      contactPoint: [
        {
          "@type": "ContactPoint",
          telephone: "+46-010-7087000",
          contactType: "customer service",
          areaServed: "SE",
          email: "info@gronalund.com",
        },
      ],
      sameAs: [
        "https://www.facebook.com/gronalundstivoli/",
        "https://www.youtube.com/user/gronalundstivoli",
        "https://www.instagram.com/gronalund/",
      ],
    },
    null,
    2,
  );

export const Head = (props: Props) => {
  const context = useContext(GlobalContext);

  const { data, pageContext, location } = props;

  const contentPage = data.contentfulContentPage;
  const siteConfig = data.contentfulSiteConfiguration;

  const isStartPage = pageContext.slug === "/";
  const {
    enableGoogleOptimizeAntiFlickerScript,
    socialMediaSharedImage,
    blocks,
    excludePageFromExternalSearch,
    description,
    title,
    metaTitle,
    node_locale,
    keywords,
  } = contentPage;
  const { googleOptimizeContainerId } = siteConfig;
  const logo = siteConfig?.footerLogo?.file?.url;

  const ogImage =
    FacebookHelper.extendContentfulUrlWithHttps(socialMediaSharedImage?.file?.url) ||
    FacebookHelper.getFacebookImageByCurrentBlocks(blocks ?? [], siteConfig);
  const facebookLocale = FacebookHelper.getFacebookLocale();
  const facebookUrl = FacebookHelper.getFaceBookUrl(location.pathname);
  const metaRobotsContent = excludePageFromExternalSearch ? "noindex,follow" : "index,follow";
  return (
    <>
      <Html isViewportScrollable={context.isViewportScrollable} lang={node_locale} />
      <Title title={title} />
      <MetaTags
        description={description}
        keywords={keywords}
        robots={metaRobotsContent}
        facebookOpenGraphData={{
          url: facebookUrl,
          title: metaTitle ?? title,
          description: description,
          image: ogImage,
          locale: facebookLocale,
        }}
      />
      <LinkTags />
      {isStartPage && <Script type="application/ld+json">{seoLinkedData(logo)}</Script>}
      {googleOptimizeContainerId && enableGoogleOptimizeAntiFlickerScript && (
        <style>{`.async-hide { opacity: 0 !important}`}</style>
      )}
      {googleOptimizeContainerId && enableGoogleOptimizeAntiFlickerScript && (
        <Script>
          {`(function(a,s,y,n,c,h,i,d,e){s.className+=' '+y;h.start=1*new Date;
          h.end=i=function(){s.className=s.className.replace(RegExp(' ?'+y),'')};
          (a[n]=a[n]||[]).hide=h;setTimeout(function(){i();h.end=null},c);h.timeout=c;
          })(window,document.documentElement,'async-hide','dataLayer',4000,
          {'${googleOptimizeContainerId}':true});`}
        </Script>
      )}
    </>
  );
};
